import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await register({
        first_name: form.first_name.trim() || null,
        last_name: form.last_name.trim() || null,
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || null,
        password: form.password,
      });
      navigate("/account", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="register-page"
      className="min-h-screen flex items-center justify-center px-4 py-16 relative overflow-hidden"
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url('/assets/booking-bg.jpg')" }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-[#0B0B0C]/80 backdrop-blur-sm" aria-hidden />

      <div className="relative z-10 w-full max-w-md">
        <Link to="/" data-testid="auth-brand-link" className="block text-center mb-10">
          <div className="font-serif text-3xl font-semibold tracking-tight text-white">
            Phantasm
          </div>
          <div className="mt-1 text-[10px] uppercase tracking-[0.35em] text-[#8E9AA6]">
            Limousine Service Inc.
          </div>
        </Link>

        <form
          onSubmit={onSubmit}
          data-testid="register-form"
          className="backdrop-blur-2xl bg-[#121214]/80 border border-white/10 p-10 shadow-2xl"
        >
          <div className="text-[11px] uppercase tracking-[0.35em] text-[#8E9AA6] mb-3">
            Account
          </div>
          <h1 className="font-serif text-3xl text-white font-semibold tracking-tight mb-10">
            Create your account
          </h1>

          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-5">
              <div>
                <label className="block text-xs uppercase tracking-[0.25em] text-[#8E9AA6] mb-3">
                  First name
                </label>
                <input
                  type="text"
                  value={form.first_name}
                  onChange={onChange("first_name")}
                  data-testid="register-first-name-input"
                  className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-white focus:border-white focus:outline-none transition-colors font-sans text-base"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.25em] text-[#8E9AA6] mb-3">
                  Last name
                </label>
                <input
                  type="text"
                  value={form.last_name}
                  onChange={onChange("last_name")}
                  data-testid="register-last-name-input"
                  className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-white focus:border-white focus:outline-none transition-colors font-sans text-base"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.25em] text-[#8E9AA6] mb-3">
                Email
              </label>
              <input
                type="email"
                required
                value={form.email}
                onChange={onChange("email")}
                data-testid="register-email-input"
                autoComplete="email"
                className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-white focus:border-white focus:outline-none transition-colors font-sans text-base"
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.25em] text-[#8E9AA6] mb-3">
                Phone
              </label>
              <input
                type="tel"
                value={form.phone}
                onChange={onChange("phone")}
                data-testid="register-phone-input"
                autoComplete="tel"
                placeholder="+1 (___) ___-____"
                className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-white placeholder-[#8E9AA6] focus:border-white focus:outline-none transition-colors font-sans text-base"
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.25em] text-[#8E9AA6] mb-3">
                Password
              </label>
              <input
                type="password"
                required
                minLength={8}
                value={form.password}
                onChange={onChange("password")}
                data-testid="register-password-input"
                autoComplete="new-password"
                className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-white focus:border-white focus:outline-none transition-colors font-sans text-base"
              />
              <div className="mt-2 text-[10px] uppercase tracking-[0.25em] text-[#8E9AA6]">
                Minimum 8 characters
              </div>
            </div>
          </div>

          {error && (
            <div
              data-testid="register-error"
              className="mt-6 text-xs text-red-400 tracking-wide"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="register-submit-btn"
            className="w-full mt-10 bg-white text-black hover:bg-[#E6E6E6] disabled:opacity-50 px-8 py-4 transition-all duration-300 font-medium text-xs tracking-[0.25em] uppercase"
          >
            {submitting ? "Creating account…" : "Create account"}
          </button>

          <div className="mt-8 text-center text-xs text-[#8E9AA6] tracking-wide">
            Already have an account?{" "}
            <Link
              to="/login"
              data-testid="register-to-login-link"
              className="text-white hover:text-[#E6E6E6] transition-colors uppercase tracking-[0.2em]"
            >
              Sign in
            </Link>
          </div>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/"
            data-testid="register-back-home"
            className="text-[10px] uppercase tracking-[0.35em] text-[#8E9AA6] hover:text-white transition-colors"
          >
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}
